import { useCallback, useMemo, useState } from 'react';
import { TableRowSelection } from 'antd/es/table/interface';
import { IFaqBase } from '@/models/faqBase.model';

const useChosenFaqs = <T extends IFaqBase>() => {
  const [chosenFaqs, setChosenFaqs] = useState<string[]>([]);

  const handleResetChosenFaqs = useCallback(() => {
    setChosenFaqs([]);
  }, []);

  const handleSelectOneFaq = useCallback((faq: T, selected: boolean) => {
    setChosenFaqs((prev) => (selected ? [...prev, faq.faqId] : prev.filter((id) => id !== faq.faqId)));
  }, []);

  const handleSelectAllFaqsOnPage = useCallback((changeableRowKeys: string[]) => {
    setChosenFaqs((prev) => Array.from(new Set([...prev, ...changeableRowKeys])));
  }, []);

  const handleResetChosenFaqsOnPage = useCallback((changeableRowKeys: string[]) => {
    setChosenFaqs((prev) => prev.filter((id) => !changeableRowKeys.includes(id)));
  }, []);

  const handleToggleAllSelectedFaqsOnPage = useCallback(
    (selected: boolean, changeableRowKeys: string[]) => {
      if (selected) {
        return handleSelectAllFaqsOnPage(changeableRowKeys);
      }
      handleResetChosenFaqsOnPage(changeableRowKeys);
    },
    [handleSelectAllFaqsOnPage, handleResetChosenFaqsOnPage],
  );

  const rowSelection: TableRowSelection<T> = useMemo(
    () => ({
      columnWidth: 32,
      selectedRowKeys: chosenFaqs,
      preserveSelectedRowKeys: true,
      onSelect: handleSelectOneFaq,
      onSelectAll: (selected, _selectedRows, changeRows) => {
        const changedKeys = changeRows.map<string>((faq) => faq.faqId);
        handleToggleAllSelectedFaqsOnPage(selected, changedKeys);
      },
    }),
    [chosenFaqs, handleSelectOneFaq, handleToggleAllSelectedFaqsOnPage],
  );

  return { chosenFaqs, rowSelection, handleResetChosenFaqs };
};

export default useChosenFaqs;
